import './text-editor.scss';

import {
  type ComponentProps,
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';

import { AllurLoader } from '..';

type TJoditInstance = {
  events: {
    on: (
      event: 'beforePaste',
      callback: (event: ClipboardEvent) => void | false
    ) => unknown;
  };
  s: {
    insertHTML: (html: string) => void;
  };
};

const JoditEditor = lazy(() => import('jodit-react'));

const cleanHTML = (
  htmlStr: string | null | undefined,
  isDocument = false
): string | null | undefined => {
  if (!htmlStr) return htmlStr;

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlStr, 'text/html');

  if (!isDocument) return doc.body.innerHTML;

  doc.body.querySelectorAll('*').forEach((el) => {
    const element = el as HTMLElement;
    element.style.fontFamily = '"Times New Roman", serif';

    element.style.removeProperty('margin');
    element.style.removeProperty('margin-left');
    element.style.removeProperty('margin-right');

    if (element.tagName === 'P') {
      element.style.setProperty('text-indent', '36px');
    } else if (element.tagName === 'TABLE') {
      element.style.margin = '0 auto';
    }

    if (element.classList.contains('page-break')) {
      element.style.removeProperty('margin');
      element.style.removeProperty('border-top');
      element.style.removeProperty('border');
      element.style.removeProperty('color');
      element.style.removeProperty('font-size');
    }
  });

  doc.body.querySelectorAll('table p').forEach((el) => {
    const p = el as HTMLElement;
    p.style.removeProperty('text-indent');
  });

  return doc.body.innerHTML;
};

type TTextEditorProps = {
  defaultHeight?: number;
  basicHTML: string | null | undefined;
  onChange: (data: string) => void;
  onBlur?: (newValue: string) => void;
  isIframe?: boolean;
  removeButtons?: string[];
  isDocument?: boolean;
};

const TextEditor = ({
  defaultHeight = 600,
  basicHTML,
  onChange,
  onBlur,
  isIframe = true,
  removeButtons,
  isDocument = false
}: TTextEditorProps) => {
  const [initialValue] = useState(() => cleanHTML(basicHTML, isDocument));

  const config = useMemo(
    () =>
      ({
        readonly: false,
        tabIndex: 1,
        iframe: isIframe,
        height: defaultHeight,
        askBeforePasteHTML: false,
        askBeforePasteFromWord: false,
        processPasteHTML: false,
        defaultActionOnPaste: 'insert_clear_html',
        cleanHTML: {
          removeEmptyElements: false,
          replaceNBSP: false
        },
        preserveSpaces: true,
        editorCssText: 'white-space: pre-wrap;',
        iframeStyle: `
          .page-break {
            display: block;
            position: relative;
            border-top: 2px dashed rgb(200, 200, 200);
            margin: 16px 0;
            text-align: center;
          }
          .page-break::after {
            content: "--- Разрыв страницы ---";
            font-size: 11px;
            color: rgb(200, 200, 200);
            display: block;
            margin-top: 4px;
          }
        `,
        uploader: {
          insertImageAsBase64URI: true
        },
        filebrowser: {
          ajax: {
            // @ts-ignore
            // Если undefined, то по дефолту отправляет запросу по /
            url: null // Отключает серверный файловый менеджер
          }
        },
        removeButtons,
        extraButtons: [
          {
            name: 'pageBreakBtn',
            tooltip: 'Разрыв страницы',
            icon: 'enter',
            exec(editor: any) {
              editor.s.insertHTML(
                '<div class="page-break" style="break-after: page;" contenteditable="false"></div><p></p>'
              );
            }
          }
        ]
      }) as unknown as ComponentProps<typeof JoditEditor>['config'],
    [isIframe, defaultHeight, removeButtons]
  );

  const handleEditorReady = useCallback(
    (editor: TJoditInstance): void => {
      editor.events.on('beforePaste', (event: ClipboardEvent): false => {
        const dataTransfer = event.clipboardData;

        if (!dataTransfer) return false;

        const pastedHtml = dataTransfer.getData('text/html');
        const pastedPlain = dataTransfer.getData('text/plain');
        const rawContent = pastedHtml || pastedPlain;

        if (!rawContent) return false;

        const cleanedContent = cleanHTML(rawContent, isDocument);

        editor.s.insertHTML(cleanedContent || '');

        return false;
      });
    },
    [isDocument]
  );

  const handleChange = (htmlStr: string): void => {
    onChange(cleanHTML(htmlStr, isDocument) || '');
  };

  useEffect(() => {
    if (initialValue !== basicHTML) {
      onChange(initialValue || '');
    }
  }, []);

  return (
    <Suspense fallback={<AllurLoader fullWidth />}>
      <JoditEditor
        value={initialValue ?? undefined}
        config={config}
        onChange={handleChange}
        onBlur={onBlur}
        editorRef={handleEditorReady}
      />
    </Suspense>
  );
};

export default TextEditor;
